import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

import { authService } from '@/services/api/authService';
import type {
  AuthState,
  LoginCredentials,
  RegisterCredentials,
  User,
  AuthResponse,
  ChangePasswordRequest,
} from '@/types';
import type {
  LoginOtpRequiredResponse,
  RegisterOtpRequiredResponse,
} from '@/types/auth';

/**
 * Extract error message from axios error or generic error
 */
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message || error.message || 'An error occurred'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
};

/**
 * Initial state for authentication
 * Tokens are now stored in HTTP-only cookies, not in client-side state
 */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading to check auth status on init
  error: null,
  requirePasswordChange: false,
  requireEmailVerification: false,
  pendingOtp: null,
  pendingRegisterOtp: null,
};

/**
 * Async thunk for user login.
 * Backend may return requiresOtp + tempToken (OTP sent to phone) or user + tokens.
 */
export const loginUser = createAsyncThunk<
  AuthResponse | LoginOtpRequiredResponse,
  LoginCredentials,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.login(credentials);
    const inner = (response as { data?: AuthResponse | LoginOtpRequiredResponse }).data ?? response;
    return inner as AuthResponse | LoginOtpRequiredResponse;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Async thunk to complete login after OTP verification
 */
export const verifyLoginOtp = createAsyncThunk<
  AuthResponse,
  { tempToken: string; otp: string },
  { rejectValue: string }
>('auth/verifyLoginOtp', async ({ tempToken, otp }, { rejectWithValue }) => {
  try {
    const response = await authService.verifyLoginOtp(tempToken, otp);
    const inner = (response as { data?: AuthResponse }).data ?? response;
    return inner as AuthResponse;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Async thunk for user registration (may return requiresOtp + tempToken)
 */
export const registerUser = createAsyncThunk<
  AuthResponse | RegisterOtpRequiredResponse,
  RegisterCredentials,
  { rejectValue: string }
>('auth/register', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.register(credentials);
    const inner = (response as { data?: AuthResponse | RegisterOtpRequiredResponse }).data ?? response;
    return inner as AuthResponse | RegisterOtpRequiredResponse;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Async thunk to complete registration after OTP verification
 */
export const verifyRegisterOtp = createAsyncThunk<
  AuthResponse,
  { tempToken: string; otp: string },
  { rejectValue: string }
>('auth/verifyRegisterOtp', async ({ tempToken, otp }, { rejectWithValue }) => {
  try {
    const response = await authService.verifyRegisterOtp(tempToken, otp);
    // Backend returns { success, message, data: { user, emailVerificationSent } }
    const raw = response as { data?: AuthResponse; user?: unknown };
    const payload = raw.data ?? (raw.user ? { user: raw.user, emailVerificationSent: (raw as { emailVerificationSent?: boolean }).emailVerificationSent } : raw);
    const auth = payload as AuthResponse;
    if (!auth?.user) {
      return rejectWithValue('Invalid response from server');
    }
    return auth;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Async thunk for user logout
 */
export const logoutUser = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      // Backend clears HTTP-only cookies on successful logout
      return;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Async thunk for getting current user
 */
export const getCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>('auth/getCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const response = await authService.getCurrentUser();
    return response.data.user;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Initialize auth state by checking if user is authenticated via HTTP-only cookie
 * This runs on app load and automatically refreshes tokens if needed
 */
export const initializeAuth = createAsyncThunk<{ user: User } | null, void>(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      // Try to get current user - cookies will be sent automatically
      // If access token is expired, axios interceptor will automatically refresh it
      const response = await authService.getCurrentUser();
      return {
        user: response.data.user,
      };
    } catch (error: any) {
      // If we get a 401, try to refresh the token once
      if (error?.response?.status === 401) {
        try {
          // Attempt to refresh token
          await authService.refreshToken();
          // Retry getting current user after refresh
          const retryResponse = await authService.getCurrentUser();
          return {
            user: retryResponse.data.user,
          };
        } catch (refreshError) {
          // Refresh failed - user needs to login again
          return null;
        }
      }
      // Not authenticated or token is invalid
      return null;
    }
  }
);

/**
 * Async thunk for changing password
 */
export const changePassword = createAsyncThunk<
  void,
  ChangePasswordRequest,
  { rejectValue: string }
>('auth/changePassword', async (data, { rejectWithValue }) => {
  try {
    await authService.changePassword(data);
    return;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Delete own account (password required). Backend clears cookies; state is cleared in extraReducers.
 */
export const requestDeleteAccountOtp = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('auth/requestDeleteAccountOtp', async (_, { rejectWithValue }) => {
  try {
    await authService.requestDeleteAccountOtp();
    return;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const confirmDeleteAccountOtp = createAsyncThunk<
  void,
  { otp: string },
  { rejectValue: string }
>('auth/confirmDeleteAccountOtp', async (data, { rejectWithValue }) => {
  try {
    await authService.confirmDeleteAccountOtp(data);
    return;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/**
 * Auth slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.requirePasswordChange = false;
      state.requireEmailVerification = false;
      state.pendingOtp = null;
      state.pendingRegisterOtp = null;
      // Note: HTTP-only cookies are cleared by the backend on logout
    },
    clearPendingOtp: (state) => {
      state.pendingOtp = null;
    },
    clearPendingRegisterOtp: (state) => {
      state.pendingRegisterOtp = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setRequirePasswordChange: (state, action: PayloadAction<boolean>) => {
      state.requirePasswordChange = action.payload;
    },
    setRequireEmailVerification: (state, action: PayloadAction<boolean>) => {
      state.requireEmailVerification = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.isAuthenticated = true;
        }
        state.isLoading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const payload = action.payload as AuthResponse | LoginOtpRequiredResponse;
        if ('requiresOtp' in payload && payload.requiresOtp) {
          state.pendingOtp = { tempToken: payload.tempToken };
          state.user = null;
          state.isAuthenticated = false;
          return;
        }
        state.pendingOtp = null;
        const auth = payload as AuthResponse;
        state.user = auth.user;
        state.isAuthenticated = true;
        state.requirePasswordChange = auth.requirePasswordChange ?? false;
        // Tokens are set as HTTP-only cookies by the backend
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Login failed';
        state.pendingOtp = null;
      });

    // Verify login OTP
    builder
      .addCase(verifyLoginOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.pendingOtp = null;
        const auth = action.payload;
        state.user = auth.user;
        state.isAuthenticated = true;
        state.requirePasswordChange = auth.requirePasswordChange ?? false;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Invalid OTP';
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const payload = action.payload as AuthResponse | RegisterOtpRequiredResponse;
        if ('requiresOtp' in payload && payload.requiresOtp) {
          state.pendingRegisterOtp = { tempToken: payload.tempToken };
          state.user = null;
          state.isAuthenticated = false;
          return;
        }
        state.pendingRegisterOtp = null;
        const auth = payload as AuthResponse;
        state.user = auth.user;
        state.isAuthenticated = true;
        state.requireEmailVerification = auth.emailVerificationSent ?? false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Registration failed';
        state.pendingRegisterOtp = null;
      });

    // Verify register OTP
    builder
      .addCase(verifyRegisterOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyRegisterOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.pendingRegisterOtp = null;
        const auth = action.payload;
        state.user = auth.user;
        state.isAuthenticated = true;
        state.requireEmailVerification = auth.emailVerificationSent ?? false;
      })
      .addCase(verifyRegisterOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Invalid OTP';
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        // HTTP-only cookies are cleared by the backend
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        // Still clear auth even if logout request fails
        state.user = null;
        state.isAuthenticated = false;
      });

    // Get current user
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to get user';
        state.isAuthenticated = false;
      });

    // Change password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to change password';
      });

    // Request delete account OTP
    builder
      .addCase(requestDeleteAccountOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestDeleteAccountOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(requestDeleteAccountOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to send OTP';
      });

    // Confirm delete account OTP
    builder
      .addCase(confirmDeleteAccountOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmDeleteAccountOtp.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.requirePasswordChange = false;
        state.requireEmailVerification = false;
        state.pendingOtp = null;
        state.pendingRegisterOtp = null;
      })
      .addCase(confirmDeleteAccountOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete account';
      });
  },
});

// Export actions
export const {
  setUser,
  clearAuth,
  clearError,
  clearPendingOtp,
  clearPendingRegisterOtp,
  setRequirePasswordChange,
  setRequireEmailVerification,
} = authSlice.actions;

// Export selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectError = (state: { auth: AuthState }) => state.auth.error;
export const selectRequirePasswordChange = (state: { auth: AuthState }) =>
  state.auth.requirePasswordChange;
export const selectRequireEmailVerification = (state: { auth: AuthState }) =>
  state.auth.requireEmailVerification;
export const selectPendingOtp = (state: { auth: AuthState }) =>
  state.auth.pendingOtp;
export const selectPendingRegisterOtp = (state: { auth: AuthState }) =>
  state.auth.pendingRegisterOtp;

export default authSlice.reducer;
